import React from "react";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";

import {
  makeStyles,
  Typography,
  Card,
  Button,
  Tooltip
} from "@material-ui/core";
import { CloudUpload as CloudUploadIcon } from "@material-ui/icons";

import AssetPreview from "../Shared/AssetPreview";
import WarningSnackbar from "../Shared/WarningSnackbar";
import CreativeDialog from "../../Shared/AssignedCreativesDialog/CreativeDialog";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center"
};

const baseStyle2 = {
  minHeight: "0vh",
  padding: 0,
  width: "fit-content",
  margin: "2vh auto"
};

const uploadIcon = {
  fontSize: "180"
};

const useStyles = makeStyles((theme) => ({
  card: {
    minWidth: 275,
    margin: "2vh auto",
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  container: {
    padding: "8vh 0 10vh"
  },
  aside: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    width: "100%"
  },
  h6: {
    fontWeight: 400,
    fontSize: "1.15rem"
  },
  button: {
    margin: theme.spacing(2)
  }
}));

const AssetUploader = ({
  assetsById,
  assetIds,
  handleDrop,
  snackbarOpen,
  setSnackbarState,
  snackbarMessage,
  isThumbnailEnabled,
  filetype,
  creativeTypes,
  selectedCreatives,
  setSelectedCreatives,
  placements,
  canChooseExisting,
  uploadLimit,
  headerExtra
}) => {
  const creativeCount = useSelector((state) => state.creatives.allIds.length);
  const { getRootProps, getInputProps } = useDropzone({
    accept: filetype,
    onDrop: handleDrop,
    disabled: assetIds.length >= uploadLimit
  });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  let classes = useStyles();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const supportedFileType = () => {
    if (filetype === "image/*") {
      return "Image(s)";
    } else if (filetype === "video/*") {
      return "Video(s)";
    } else if (filetype === "audio/*") {
      return "Audio File(s)";
    } else if (filetype === ".zip") {
      return ".zip file";
    } else {
      return null;
    }
  };

  let button;
  if (creativeCount <= 0) {
    button = (
      <Button
        variant="contained"
        color="secondary"
        className={classes.button}
        disableElevation
      >
        Add {supportedFileType()}
      </Button>
    );
  } else if (creativeCount > 0 && creativeCount < uploadLimit) {
    button = (
      <Button
        variant="contained"
        color="secondary"
        className={classes.button}
        disableElevation
      >
        Add More Creatives
      </Button>
    );
  } else {
    button = (
      <Tooltip title={`Upload limit is ${uploadLimit} files`} placement="top">
        <Button
          variant="contained"
          color="disabled"
          className={classes.button}
          disableElevation
        >
          Add More Creatives
        </Button>
      </Tooltip>
    );
  }

  const supportedFileTypes = () => {
    if (filetype === "image/*") {
      return "Supported file types: gif, jpg, jpeg, png, wbmp, adz";
    } else if (filetype === "video/*") {
      return "Supported file types: avi, mov, mp4, m4v, mpeg, mpg, oga, ogg, ogv, webm, wmv";
    } else if (filetype === "audio/*") {
      return "Supported file types: mp3, oga, ogg, ogv";
    } else {
      return null;
    }
  };

  return (
    <React.Fragment>
      <WarningSnackbar
        open={snackbarOpen}
        setSnackbarState={setSnackbarState}
        message={snackbarMessage}
        severity={snackbarMessage ? "error" : "warning"}
      />
      {creativeCount <= 0 ? (
        <React.Fragment>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start"
            }}
          >
            <div>
              <Typography variant="h6" className={classes.h6}>
                Select creative(s) to upload (limit {uploadLimit} files)
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {supportedFileTypes()}
              </Typography>
            </div>
          </div>
          {headerExtra}
          <Card className={classes.card}>
            <section>
              <aside className={classes.aside}>
                {assetIds.map((id) => {
                  return (
                    <AssetPreview
                      key={id}
                      asset={assetsById[id]}
                      isThumbnailEnabled={isThumbnailEnabled}
                      uploadLimit={uploadLimit}
                    />
                  );
                })}
              </aside>
              <div {...getRootProps()} style={{ ...baseStyle }}>
                <input {...getInputProps()} />
                <CloudUploadIcon color="disabled" style={uploadIcon} />
                <Typography variant="subtitle1" color="textSecondary">
                  Drag and drop the files you want to upload
                </Typography>
                {button}
              </div>
            </section>
            {canChooseExisting ? (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClickOpen}
              >
                Choose from existing
              </Button>
            ) : null}
          </Card>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Typography variant="h6" className={classes.h6}>
            Select creative(s) to upload (limit {uploadLimit} files)
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {supportedFileTypes()}
          </Typography>
          {headerExtra}
          <Card className={classes.card}>
            <section className={classes.section}>
              <aside className={classes.aside}>
                {assetIds.map((id) => {
                  return (
                    <AssetPreview
                      key={id}
                      asset={assetsById[id]}
                      isThumbnailEnabled={isThumbnailEnabled}
                      uploadLimit={uploadLimit}
                    />
                  );
                })}
              </aside>
              <div {...getRootProps()} style={{ ...baseStyle, ...baseStyle2 }}>
                <input {...getInputProps()} />
                {button}
              </div>
            </section>
            {canChooseExisting && creativeCount < uploadLimit ? (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClickOpen}
              >
                Choose from existing
              </Button>
            ) : null}
          </Card>
        </React.Fragment>
      )}
      <CreativeDialog
        backup={false}
        open={open}
        setOpen={setOpen}
        creativeTypes={creativeTypes}
        selectedCreatives={selectedCreatives}
        setSelectedCreatives={setSelectedCreatives}
        placements={placements}
        // selectedIds={selectedIds}
        // deselectCards={deselectCards}
        handleCloseMenu={handleClose}
      />
    </React.Fragment>
  );
};

export default AssetUploader;
